import { uniqueId } from 'lodash-es'

/** KPIs
  * Define all kpis:
  * - name: translation key for KPI name
  * - category: category of the KPI, list is grouped by categories
  * - kpi: the KPI name to append to url
  * - entities: type of entity that have data for this KPI ("all" means all customers)
  * - attrs: attributes used to request chart data
  * - groupBy: attribute to use to group data (for channels and spread factor KPIs)
  * - alarms: attributes used to request alarms data
  * - unit: data unit
  * - link: link key to get url for webservices
  * - metricNames: metrics to get for BSC statistics
  * - connect: connect line/bars in chart. Default: false
  * - chartType: type of the chart [line/bars]. Default: line
  */

/** GATEWAY STATISTICS **/
const gateway = [
  {
    name: 'kpi:kpiList.gateway.linkStatus',
    category: 'gateway',
    entities: ['gateway'],
    attrs: ['connected', 'disconnected'],
    alarms: '',
    unit: '',
    link: 'gatewayConnections'
  }, {
    name: 'kpi:kpiList.gateway.cpu',
    category: 'gateway',
    entities: ['gateway'],
    attrs: ['cpuMin', 'cpuAvg', 'cpuMax', 'cpuThreshold', 'cpuHysteresis'],
    alarms: 'CPU_ON',
    unit: '%',
    link: 'gatewayNumericStatistics',
    metricNames: '@cpu'
  }, {
    name: 'kpi:kpiList.gateway.ram',
    category: 'gateway',
    entities: ['gateway'],
    attrs: ['ramMin', 'ramAvg', 'ramMax', 'ramThreshold', 'ramHysteresis'],
    alarms: 'RAM_ON',
    unit: '%',
    link: 'gatewayNumericStatistics',
    metricNames: '@ram'
  }, {
    name: 'kpi:kpiList.gateway.systemDisk',
    category: 'gateway',
    entities: ['gateway'],
    attrs: ['systemDiskUsed', 'systemDiskThreshold', 'systemDiskHysteresis'],
    alarms: 'DISK_SYSTEM_ON',
    unit: '%',
    link: 'gatewayNumericStatistics',
    metricNames: '@system'
  }, {
    name: 'kpi:kpiList.gateway.userDisk',
    category: 'gateway',
    entities: ['gateway'],
    attrs: ['userDiskUsed', 'userDiskThreshold', 'userDiskHysteresis'],
    alarms: 'DISK_USER_ON',
    unit: '%',
    link: 'gatewayNumericStatistics',
    metricNames: '@user'
  }, {
    name: 'kpi:kpiList.gateway.rssi',
    category: 'gateway',
    entities: ['gateway'],
    attrs: ['gsmMinRssi', 'gsmAvgRssi', 'gsmMaxRssi', 'gsmRssiThreshold', 'gsmRssiHysteresis'],
    alarms: 'RSSI_ON',
    unit: 'dBm',
    link: 'gatewayNumericStatistics',
    metricNames: '@gsm'
  }, {
    name: 'kpi:kpiList.gateway.temperature',
    category: 'gateway',
    entities: ['gateway'],
    attrs: ['temperatureMin', 'temperatureAvg', 'temperatureMax', 'temperatureThreshold', 'temperatureHysteresis'],
    alarms: 'TEMPERATURE_ON',
    unit: '°C',
    link: 'gatewayNumericStatistics',
    metricNames: '@temperature'
  }, {
    name: 'kpi:kpiList.gateway.supplyPowerMilliVolts',
    category: 'gateway',
    entities: ['gateway'],
    attrs: ['supplyPowerMilliVolts'],
    alarms: '',
    unit: 'mV',
    link: 'gatewayNumericStatistics',
    metricNames: 'supplyPowerMilliVolts'
  }, {
    name: 'kpi:kpiList.gateway.supplyPowerSource',
    category: 'gateway',
    entities: ['gateway'],
    attrs: ['supplyPowerSource'],
    alarms: '',
    unit: '',
    link: 'gatewayTermStatistics',
    metricNames: 'supplyPowerSource',
    chartType: 'bars'
  }, {
    name: 'kpi:kpiList.gateway.rxperAvg',
    category: 'gateway',
    entities: ['gateway'],
    attrs: ['rxperAvg'],
    alarms: '',
    unit: '%',
    link: 'gatewayNumericStatistics',
    metricNames: 'rxperAvg'
  }, {
    name: 'kpi:kpiList.gateway.restartSum',
    category: 'gateway',
    entities: ['gateway'],
    attrs: ['restartSum'],
    alarms: '',
    unit: '',
    link: 'gatewayNumericStatistics',
    metricNames: 'restartSum',
    chartType: 'bars'
  }, {
    name: 'kpi:kpiList.gateway.maliciousSshLoginSum',
    category: 'gateway',
    entities: ['gateway'],
    attrs: ['maliciousSshLoginSum'],
    alarms: '',
    unit: '',
    link: 'gatewayNumericStatistics',
    metricNames: 'maliciousSshLoginSum',
    chartType: 'bars'
  }
]

/** UPLINK KPIs **/
const uplink = [
  // Message
  {
    kpi: 'NB_UPLINK',
    category: 'uplink',
    entities: ['all', 'customer', 'gateway', 'endDevice'],
    attrs: ['value'],
    unit: '',
    link: 'rncKpis'
  }, {
    kpi: 'UPLINK_BYTES',
    category: 'uplink',
    entities: ['all', 'customer', 'gateway', 'endDevice'],
    attrs: ['value'],
    unit: 'B',
    link: 'rncKpis'
  }, {
    kpi: 'RSSI_UPLINK_MESSAGE',
    category: 'uplink',
    entities: ['gateway'],
    attrs: ['min', 'avg', 'max'],
    unit: 'dBm',
    link: 'rncKpis'
  }, {
    kpi: 'SNR_UPLINK_MESSAGE',
    category: 'uplink',
    entities: ['gateway'],
    attrs: ['min', 'avg', 'max'],
    unit: 'dB',
    link: 'rncKpis'
  }, {
    // Message Toa
    kpi: 'TOA_UPLINK_MESSAGE',
    category: 'uplink',
    entities: ['all', 'customer', 'gateway', 'endDevice'],
    attrs: ['value'],
    unit: 'ns',
    link: 'rncKpis'
  }, {
    kpi: 'TOA_UPLINK_MESSAGE',
    category: 'uplink',
    entities: ['all', 'customer', 'gateway', 'endDevice'],
    // min/max/avg only for end-devices and for day+
    attrs: ['ratio', 'min', 'avg', 'max'],
    unit: '%',
    link: 'rncKpis'
  }, {
    // Message Toa by channel
    kpi: 'TOA_UPLINK_MESSAGE',
    attrs: ['value'],
    category: 'uplink',
    entities: ['endDevice'],
    groupBy: 'frq',
    unit: 'ns',
    link: 'rncKpis'
  }, {
    kpi: 'TOA_UPLINK_MESSAGE',
    attrs: ['ratio'],
    category: 'uplink',
    entities: ['endDevice'],
    groupBy: 'frq',
    unit: '%',
    link: 'rncKpis'
  }, {
    // Message Toa by spread factor
    kpi: 'TOA_UPLINK_MESSAGE',
    attrs: ['value'],
    category: 'uplink',
    entities: ['endDevice'],
    groupBy: 'sf',
    unit: 'ns',
    link: 'rncKpis'
  }, {
    kpi: 'TOA_UPLINK_MESSAGE',
    attrs: ['ratio'],
    category: 'uplink',
    entities: ['endDevice'],
    groupBy: 'sf',
    unit: '%',
    link: 'rncKpis'
  }
]

/** DOWNLINK KPIs **/
const downlink = [
  // Message
  {
    kpi: 'NB_DOWNLINK',
    category: 'downlink',
    entities: ['all', 'customer', 'gateway', 'endDevice'],
    attrs: ['value'],
    unit: '',
    link: 'rncKpis'
  }, {
    kpi: 'DOWNLINK_BYTES',
    category: 'downlink',
    entities: ['all', 'customer', 'gateway', 'endDevice'],
    attrs: ['value'],
    unit: 'B',
    link: 'rncKpis'
  }, {
    // Message Toa
    kpi: 'TOA_DOWNLINK_MESSAGE',
    category: 'downlink',
    entities: ['all', 'customer', 'gateway', 'endDevice'],
    attrs: ['value'],
    unit: 'ns',
    link: 'rncKpis'
  }, {
    kpi: 'TOA_DOWNLINK_MESSAGE',
    category: 'downlink',
    // min/max/avg only for customers and gateways and for day+
    entities: ['all', 'customer', 'gateway', 'endDevice'],
    attrs: ['ratio', 'min', 'avg', 'max'],
    unit: '%',
    link: 'rncKpis'
  }, {
    // Message Toa by channel
    kpi: 'TOA_DOWNLINK_MESSAGE',
    attrs: ['value'],
    category: 'downlink',
    entities: ['all', 'customer', 'gateway'],
    groupBy: 'frq',
    unit: 'ns',
    link: 'rncKpis'
  }, {
    kpi: 'TOA_DOWNLINK_MESSAGE',
    attrs: ['ratio'],
    category: 'downlink',
    entities: ['all', 'customer', 'gateway'],
    groupBy: 'frq',
    unit: '%',
    link: 'rncKpis'
  }, {
    // Message Toa by spread factor
    kpi: 'TOA_DOWNLINK_MESSAGE',
    attrs: ['value'],
    category: 'downlink',
    entities: ['all', 'customer', 'gateway'],
    groupBy: 'sf',
    unit: 'ns',
    link: 'rncKpis'
  }, {
    kpi: 'TOA_DOWNLINK_MESSAGE',
    attrs: ['ratio'],
    category: 'downlink',
    entities: ['all', 'customer', 'gateway'],
    groupBy: 'sf',
    unit: '%',
    link: 'rncKpis'
  }
]

/* NETWORK KPIs */
const network = [
  {
    kpi: 'PING',
    attrs: ['min', 'avg', 'max'],
    category: 'network',
    entities: ['all', 'customer', 'gateway'],
    unit: 'ms',
    link: 'rncKpis',
    connect: true,
    chartType: 'bars'
  }
]

// Set name for RNC KPIs according to their category and kpi description
let rncKpis = [...uplink, ...downlink, ...network]
rncKpis.map(kpi => {
  kpi.name = `kpi:kpiList.${kpi.category}.${kpi.kpi}` +
    (kpi.groupBy ? `_${kpi.groupBy}` : '') +
    (kpi.unit ? `_${kpi.unit}` : '')
})

// Create unique identifier for each KPI
const allKpis = [...gateway, ...rncKpis]
allKpis.map(kpi => { kpi.id = uniqueId('kpi_') })

export default allKpis
